// ./src/utils/api/WorkflowItemService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Workflow } from "./WorkflowService";
import { Connector } from "./ConnectorService";
import { AccountEmail } from "./AccountEmailService";

// GraphQL queries
const WORKFLOW_ITEM_LIST_QUERY = gql`
  query WorkflowItemList(
    $filter: WorkflowItemFilter
    $params: PaginationAndSorting
  ) {
    workflowItemList(filter: $filter, params: $params) {
      code
      data {
        id
        workflowId
        orderNo
        conditionType
        formFieldName
        compareOperation
        fieldValue
        customCondition
        actionType
        connectorId
        dataConnectorId
        accountEmailId
        submissionStatus
        rejectReason
        transformConfig
        stopOnConditionMet
        status
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
        connector {
          name
        }
        dataConnector {
          name
        }
        accountEmail {
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const WORKFLOW_ITEM_GET_QUERY = gql`
  query WorkflowItemGet($id: ID) {
    workflowItemGet(id: $id) {
      code
      data {
        id
        workflowId
        orderNo
        conditionType
        formFieldName
        compareOperation
        fieldValue
        customCondition
        actionType
        connectorId
        dataConnectorId
        accountEmailId
        submissionStatus
        rejectReason
        transformConfig
        stopOnConditionMet
        status
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        connector {
          name
        }
        dataConnector {
          name
        }
        accountEmail {
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const WORKFLOW_ITEM_GET_MANY_QUERY = gql`
  query WorkflowItemGetMany($ids: [ID]) {
    workflowItemGetMany(ids: $ids) {
      code
      data {
        id
        workflowId
        orderNo
        conditionType
        formFieldName
        compareOperation
        fieldValue
        customCondition
        actionType
        connectorId
        dataConnectorId
        accountEmailId
        submissionStatus
        rejectReason
        transformConfig
        stopOnConditionMet
        status
        version
        createdBy
        updatedBy
        createdAt
        updatedAt
        connector {
          name
        }
        dataConnector {
          name
        }
        accountEmail {
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const WORKFLOW_ITEM_CREATE_MUTATION = gql`
  mutation WorkflowItemCreate($params: WorkflowItemInput) {
    workflowItemCreate(params: $params) {
      code
      data {
        id
        workflowId
        orderNo
        conditionType
        formFieldName
        compareOperation
        fieldValue
        customCondition
        actionType
        connectorId
        dataConnectorId
        accountEmailId
        submissionStatus
        rejectReason
        transformConfig
        stopOnConditionMet
        status
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        connector {
          name
        }
        dataConnector {
          name
        }
        accountEmail {
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const WORKFLOW_ITEM_UPDATE_MUTATION = gql`
  mutation WorkflowItemUpdate(
    $where: WorkflowItemWhereInput
    $params: WorkflowItemInput
  ) {
    workflowItemUpdate(where: $where, params: $params) {
      code
      data {
        id
        workflowId
        orderNo
        conditionType
        formFieldName
        compareOperation
        fieldValue
        customCondition
        actionType
        connectorId
        dataConnectorId
        accountEmailId
        submissionStatus
        rejectReason
        transformConfig
        stopOnConditionMet
        status
        version
        createdBy
        updatedBy
        removedBy
        createdAt
        updatedAt
        connector {
          name
        }
        dataConnector {
          name
        }
        accountEmail {
          email
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const WORKFLOW_ITEM_REMOVE_MUTATION = gql`
  mutation WorkflowItemRemove($where: WorkflowItemWhereInput) {
    workflowItemRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const WORKFLOW_ITEM_REMOVE_MANY_MUTATION = gql`
  mutation WorkflowItemRemoveMany($where: [WorkflowItemWhereInput]) {
    workflowItemRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface WorkflowItem {
  id?: string | undefined;
  workflowId?: string | undefined;
  orderNo?: number | undefined;
  conditionType?: string | undefined;
  formFieldName?: string | undefined;
  compareOperation?: string | undefined;
  fieldValue?: string | undefined;
  customCondition?: string | undefined;
  actionType?: string | undefined;
  connectorId?: string | undefined;
  dataConnectorId?: string | undefined;
  accountEmailId?: string | undefined;
  submissionStatus?: number | undefined;
  rejectReason?: string | undefined;
  transformConfig?: string | undefined;
  stopOnConditionMet?: boolean | undefined;
  status?: number | undefined;
  version?: number | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  removedBy?: string | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
  userRemoved?: User;
  workflow?: Workflow;
  connector?: Connector;
  dataConnector?: Connector;
  accountEmail?: AccountEmail;
}

export interface WorkflowItemInput {
  id?: string | null | undefined;
  workflowId?: string | null | undefined;
  orderNo?: number | null | undefined;
  conditionType?: string | null | undefined;
  formFieldName?: string | null | undefined;
  compareOperation?: string | null | undefined;
  fieldValue?: string | null | undefined;
  customCondition?: string | null | undefined;
  actionType?: string | null | undefined;
  connectorId?: string | null | undefined;
  dataConnectorId?: string | null | undefined;
  accountEmailId?: string | null | undefined;
  submissionStatus?: number | null | undefined;
  rejectReason?: string | null | undefined;
  transformConfig?: string | null | undefined;
  stopOnConditionMet?: boolean | null | undefined;
  status?: number | null | undefined;
}

export interface WorkflowItemFilter {
  id?: string[] | null | undefined;
  workflowId?: string[] | null | undefined;
  connectorId?: string[] | null | undefined;
  dataConnectorId?: string[] | null | undefined;
  accountEmailId?: string[] | null | undefined;
  conditionType?: string[] | null | undefined;
  actionType?: string[] | null | undefined;
  status?: number[] | null | undefined;
  searchKeyword?: string | null | undefined;
}

export interface WorkflowItemWhereInput {
  id?: string | undefined;
}

/**
 * WorkflowItem service that handles all workflow item-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class WorkflowItemService {
  /**
   * Get a list of workflow items with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: WorkflowItemFilter,
    pagination?: Pagination,
    sortBy?: SortItem[],
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(WORKFLOW_ITEM_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single workflow item by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(WORKFLOW_ITEM_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple workflow items by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(WORKFLOW_ITEM_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new workflow item
   */
  static async create(params: WorkflowItemInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      WORKFLOW_ITEM_CREATE_MUTATION,
      {
        params,
      },
    );
    return result;
  }

  /**
   * Update an existing workflow item
   */
  static async update(
    where: WorkflowItemWhereInput,
    params: WorkflowItemInput,
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      WORKFLOW_ITEM_UPDATE_MUTATION,
      {
        where,
        params,
      },
    );
    return result;
  }

  /**
   * Remove/delete a workflow item
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      WORKFLOW_ITEM_REMOVE_MUTATION,
      {
        where: { id },
      },
    );
    return result;
  }

  /**
   * Remove/delete multiple workflow items
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      WORKFLOW_ITEM_REMOVE_MANY_MUTATION,
      {
        where,
      },
    );
    return result;
  }
}
